import { Box } from '@mui/material';
import { useViewModelState } from '@state/hook/useViewModel';
import React, { memo, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

interface TypingEffectProps {
  text: string;
  isUser: boolean;
  messageId: string;
  onTypingUpdate?: () => void;
}

// Tracks messages whose typing animation has already finished, so reopening the
// chat dialog (which remounts every bubble) does not replay the animation for
// historical messages. Keyed by message id + final text so the genuine
// "Processing request..." -> response transition still animates.
const completedAnimations = new Set<string>();

// Keep the module-level set bounded over long sessions. Evicting the oldest
// entries only means a very old message would re-animate if shown again.
const MAX_COMPLETED_ANIMATIONS = 500;

function markAnimationCompleted(key: string): void {
  completedAnimations.add(key);
  if (completedAnimations.size > MAX_COMPLETED_ANIMATIONS) {
    const oldest = completedAnimations.values().next().value;
    if (oldest !== undefined) {
      completedAnimations.delete(oldest);
    }
  }
}

export const TypingEffect: React.FC<TypingEffectProps> = memo(({ text, isUser, messageId, onTypingUpdate }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const settings = useViewModelState('settings');
  const inIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, []);
  const containerStyle = useMemo<React.CSSProperties>(() => (
    inIframe
      ? { contain: 'layout style', willChange: 'auto', overflowWrap: 'anywhere', wordBreak: 'break-word' }
      : {}
  ), [inIframe]);

  useEffect(() => {
    // Skip the animation for user messages, iframe embeds, and any message
    // whose animation already completed (e.g. historical messages shown again
    // after the dialog is reopened) — show the full text immediately.
    const animationKey = `${messageId} ${text}`;
    if (isUser || inIframe || completedAnimations.has(animationKey)) {
      setDisplayedText(text);
      setIsTyping(false);
      return;
    }

    // Genuinely new content: (re)start the animation. Ensure isTyping is true so
    // the cursor shows and the live region stays quiet while text streams in
    // (e.g. when text changes from "Processing request..." to the response).
    setIsTyping(true);
    let currentIndex = 0;
    const typingSpeed = 10; // Slightly slower for better scroll compatibility
    const typingInterval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayedText(text.slice(0, currentIndex));
        currentIndex++;

        // Notify parent component about typing updates for auto-scroll
        if (onTypingUpdate) {
          onTypingUpdate();
        }
      } else {
        markAnimationCompleted(animationKey);
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, typingSpeed);

    return () => clearInterval(typingInterval);
  }, [text, isUser, inIframe, messageId]);

  return (
    <Box style={containerStyle}>
      {/* Visual typing effect for users */}
      <div className={`chat-message-content ${isUser ? 'user' : ''}`}>
        <ReactMarkdown
          rehypePlugins={[
            rehypeKatex,
            [rehypeSanitize, {
              attributes: {
                '*': ['className', 'aria-label', 'aria-hidden', 'role', 'aria-busy', 'aria-live', 'aria-atomic'],
                'a': ['href', 'target'],
                'img': ['src', 'alt'],
                'math': ['display'],
                'span': ['style'],
                'svg': ['aria-hidden', 'role', 'xmlns', 'width', 'height', 'viewBox'],
                'path': ['d'],
              },
              tagNames: [
                'p',
                'br',
                'b',
                'i',
                'em',
                'strong',
                'a',
                'pre',
                'code',
                'ul',
                'ol',
                'li',
                'blockquote',
                'img',
                'math',
                'span',
                'svg',
                'path',
              ],
            }],
          ]}
          remarkPlugins={[remarkGfm, remarkMath]}
          components={{
            pre: ({ node, ...props }) => (
              <pre {...props} role="text" aria-label="Code block" />
            ),
            a: ({ node, ...props }) => (
              <a {...props} aria-label={`Link: ${props.children}`} />
            ),
            img: ({ node, ...props }) => (
              <img {...props} alt={props.alt || 'Image in message'} />
            ),
          }}
        >
          {displayedText}
        </ReactMarkdown>
      </div>
      {/* Visually hidden live region for screen readers */}
      <div
        className="sr-only"
        aria-live={settings.general.ariaMode}
        aria-atomic="true"
      >
        {isTyping ? '' : text}
      </div>
      {isTyping && !inIframe && (
        <span
          className="typing-cursor"
          aria-hidden="true"
          role="presentation"
        >
          |
        </span>
      )}
    </Box>
  );
});

TypingEffect.displayName = 'TypingEffect';
